import { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useUser } from "@context/contexts";
import { useApi } from "@hooks/useApi";
import { toast } from "react-toastify";
import "@css/Notifications.css";

const markDisplayed = async (api, ids = []) => {
    if (!ids || ids.length === 0) return;
    try {
        await api.post("notifications/mark-displayed", {
            notificationIds: ids,
        });
    } catch (err) {
        console.error("Failed to mark notifications displayed:", err);
    }
};

const FullscreenNotice = ({ note, onClose }) => {
    const [seconds, setSeconds] = useState(5);

    useEffect(() => {
        const t = setInterval(() => setSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="notif-fullscreen">
            <div className="notif-fullscreen-inner">
                <h2 className={`notif-${note.type}`}>Важное сообщение</h2>
                <div className="notif-content">{note.content}</div>
                <div className="notif-footer">
                    <button
                        className="notif-close"
                        onClick={() => onClose(note.id)}
                        disabled={seconds > 0}
                    >
                        Закрыть{seconds > 0 ? ` (${seconds})` : ""}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Notifications = () => {
    const { preferences, launchMultipleFireworks } = useUser();
    const api = useApi();
    const notifications = preferences?.notifications;

    // Priority-5 notices are shown full screen one at a time, until closed
    const [closedIds, setClosedIds] = useState(() => new Set());
    const currentFullscreen = (notifications ?? []).find(n => n.priority === 5 && !closedIds.has(n.id)) ?? null;

    // Ids already shown (toast or full screen) and reported to the API
    const announcedRef = useRef(new Set());

    useEffect(() => {
        const announced = announcedRef.current;

        // Toasts wait until every full-screen notice has been closed
        if (currentFullscreen) {
            if (!announced.has(currentFullscreen.id)) {
                announced.add(currentFullscreen.id);
                void markDisplayed(api, [currentFullscreen.id]);
            }
            return;
        }

        const others = (notifications ?? []).filter(n => n.priority !== 5 && !announced.has(n.id));
        if (others.length === 0) return;

        others.forEach(n => {
            announced.add(n.id);
            if (n?.firework) {
                launchMultipleFireworks();
            }
            switch (n.type) {
                case "success":
                    toast.success(n.content);
                    break;
                case "warning":
                    toast.warn(n.content);
                    break;
                case "error":
                    toast.error(n.content);
                    break;
                default:
                    toast.info(n.content);
            }
        });
        void markDisplayed(api, others.map(o => o.id));
    }, [notifications, currentFullscreen, api, launchMultipleFireworks]);

    const handleCloseFullscreen = useCallback((id) => {
        setClosedIds(prev => new Set(prev).add(id));
    }, []);

    if (!currentFullscreen) return null;

    // Render fullscreen notice into a stable portal (document.body)
    return typeof document !== "undefined"
        ? createPortal(
            <FullscreenNotice
                key={currentFullscreen.id}
                note={currentFullscreen}
                onClose={handleCloseFullscreen}
            />,
            document.body,
        )
        : null;
};

FullscreenNotice.propTypes = {
    note: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        type: PropTypes.string,
        content: PropTypes.node,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export default Notifications;
